import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { graphql, Link, useStaticQuery } from 'gatsby';
import { useSelector } from 'react-redux';
import { Column, Container, Icon, Row } from '@pella/sash-framework';
import { getCookie } from '../../utilities/functions';
import { useOktaAuth } from '../../../hooks/use-okta-auth';
import { useBranchContext } from '../../../context/BranchContext';
import Logo from '../../common/logo';
import GlobalNav from './global-nav';
import Navigation from './navigation';
import HomepageToggle from './homepage-toggle';
import CartList from './cart-list';
import MobileMenu from '../mobile-menu';
import SiteAccount from './accounts';
import SiteSearch from './search';
import { useCartHeader } from '../../../hooks/use-cart-header';
import './index.css';

const Header = ({ showToggle, hideHoverCart, signInToggle }) => {
	const compData = useStaticQuery(query).contentstackMenu;
	const locations = useSelector((state) => state.locations);
	const { isLogged, profile } = useOktaAuth();
	const { cart } = useCartHeader();
	const { currentBranch } = useBranchContext();
	const accountRef = useRef(null);
	const searchRef = useRef(null);
	const scrollYOffset = useRef(0);
	const [showSearch, setShowSearch] = useState(false);
	const [showAccounts, setShowAccounts] = useState(false);
	const [isEcommerce, setIsEcommerce] = useState(false);
	// Kept this userSignedIn useState so setCustomerType could still occur.
	const [userSignedIn, setUserSignedIn] = useState(false);
	const [customerType, setCustomerType] = useState('');
	const [showMobileMenu, setShowMobileMenu] = useState(false);

	const cartItemCount = cart?.total_number || 0;

	useEffect(() => {
		if (getCookie('CustomerType') != null) {
			setCustomerType(getCookie('CustomerType'));
		}

		document.addEventListener('scroll', onPageScroll);
		return () => document.removeEventListener('scroll', onPageScroll);
	}, []);

	useEffect(() => {
		if (isLogged !== userSignedIn) {
			setUserSignedIn(isLogged);

			if (getCookie('CustomerType') == null && profile.isPro) {
				setCustomerType('Professional');
			}
		}
	}, [isLogged, profile.isPro, userSignedIn]);

	useEffect(() => {
		if (typeof locations.ecommerce === 'boolean' && locations.ecommerce !== isEcommerce) {
			setIsEcommerce(locations.ecommerce);
		}
	}, [locations.ecommerce, isEcommerce]);

	const onAccountIconClick = (e) => {
		e.stopPropagation();
		setShowAccounts(!showAccounts);
		document.addEventListener('click', onClickOutsideAccounts);
	};

	const onAuthCloseModal = () => {
		document.getElementsByTagName('html')[0].click();
		setShowAccounts(!showAccounts);
	};

	const onClickOutsideAccounts = (e) => {
		if (e.target && accountRef.current && !accountRef.current.contains(e.target)) {
			document.removeEventListener('click', onClickOutsideAccounts);
			setShowAccounts(false);
		}
	};

	const onPageScroll = () => {
		const siteDiv = document.body;
		const offset = window.scrollY;

		// Remove updates from scrolling
		if (offset === 0 || (offset <= 150 && window.innerWidth > 1030)) {
			siteDiv?.classList.remove('isScrollingDown');
			siteDiv?.classList.remove('isScrollingUp');
		}

		// Trigger the desktop slimmer menu
		if (offset > 150 && offset > scrollYOffset.current) {
			siteDiv?.classList.remove('isScrollingUp');
			siteDiv?.classList.add('isScrollingDown');
		}

		// Trigger the re-appearance in mobile on scroll up
		if (offset > 150 && offset < scrollYOffset.current) {
			siteDiv?.classList.remove('isScrollingDown');
			siteDiv?.classList.add('isScrollingUp');
		}

		scrollYOffset.current = offset;
	};

	const onSearchClick = (e) => {
		e.stopPropagation();

		setShowSearch(!showSearch);
		document.addEventListener('click', onClickOutsideSearch);
	};

	const onClickOutsideSearch = (e) => {
		if (e.target && searchRef?.current && !searchRef.current.contains(e.target)) {
			document.removeEventListener('click', onClickOutsideSearch);
			setShowSearch(false);
		}
	};

	const onHamburgerClick = () => {
		setShowMobileMenu(!showMobileMenu);
		document.querySelector('.pellaContentWrapper')?.classList?.toggle('-translate-x-full');
		document.querySelector('.pellaContentWrapper')?.classList?.toggle('sm:-translate-x-[320px]');
		document.querySelector('.pellaContentWrapper')?.classList?.toggle('xl:translate-x-0');
		document.querySelector('.pellaLayout')?.classList?.toggle('max-xl:overflow-hidden');

		document.querySelector('.ot-floating-button')?.classList?.toggle('!-z-1');
		document.querySelector('.quiq-floating-element')?.classList?.toggle('!-z-1');
	};

	return (
		<>
			<header
				id='js-header'
				className={`header header--header fixed left-0 top-0 z-20 w-full border-b border-b-gray-15 bg-white before:absolute before:left-0 before:top-0 before:h-10 before:w-full before:bg-gradient-to-l before:from-gray-5 before:content-none xl:before:content-[""]`}
			>
				<Container
					className='relative'
					noPadding
				>
					<Row
						align='center'
						className='z-10 pl-2.5 3xl:pl-10'
						noGutters={true}
					>
						<Column xs='auto'>
							<Link
								to={currentBranch?.branch_url ? currentBranch.branch_url : customerType?.toLowerCase() === 'professional' ? '/professionals/' : '/'}
								title='Link to Pella.com homepage'
								className='header-logo block w-[58px] motion-safe:transition-all sm:w-[62px] xl:w-[90px]'
							>
								<Logo className='relative z-10 block h-[58px] max-w-full p-1.5 motion-safe:transition-all motion-safe:duration-300 sm:h-[62px] xl:h-[90px]' />
							</Link>
						</Column>

						<Column
							xs='fluid'
							className='!static'
						>
							{compData.global_nav?.cta?.[0] && <GlobalNav data={compData.global_nav.cta} />}

							<Row
								noGutters={true}
								justify='end'
							>
								{compData.product_menu?.[0] && (
									<Column
										xs='fluid'
										className={`!static hidden xl:block`}
									>
										<Navigation data={compData.product_menu} />
									</Column>
								)}

								{/* Toggle */}
								{showToggle && (
									<Column
										xs='auto'
										className='hidden self-center xl:block'
									>
										<HomepageToggle
											homeLink={compData.toggle?.homeowner?.[0]}
											proLink={compData.toggle?.pro?.[0]}
										/>
									</Column>
								)}

								{/* Search Icon and Form */}
								<Column
									xs='auto'
									className='header-searchCol !static'
								>
									<div
										className='h-full'
										ref={searchRef}
									>
										<button
											className={`searchTrigger relative inline-flex h-[54px] w-[50px] items-center justify-center leading-none sm:h-full sm:w-full sm:px-5 sm:py-4 lg:px-4 xl:border-l xl:border-l-gray-15 ${showSearch ? 'searchTrigger-active' : ''}`}
											onClick={(e) => onSearchClick(e)}
											aria-label='Search'
										>
											<Icon name='search' />
											<span className='ml-2.5 hidden text-xs font-semibold leading-tight 2xl:inline-flex 2xl:max-w-[17ch] 2xl:overflow-hidden 2xl:whitespace-nowrap 2xl:text-right 2xl:align-middle'>Search</span>
										</button>

										{showSearch && <SiteSearch mobileSearch={showSearch} />}
									</div>
								</Column>

								{/* Accounts Icon */}
								<Column
									xs='auto'
									className='header-AccountsCol max-sm:static'
								>
									<>
										<button
											className='inline-flex h-[54px] w-[50px] items-center justify-center leading-none sm:h-full sm:w-full sm:px-5 sm:py-4 lg:px-4 xl:border-l xl:border-l-gray-15'
											onClick={!signInToggle && !showAccounts ? (e) => onAccountIconClick(e) : null}
											aria-label='Account'
										>
											{userSignedIn && isLogged ? (
												<span
													className='accountAvatar flex h-6 w-6 items-center justify-center rounded-full border border-black text-[10px] font-semibold'
													aria-hidden={true}
												>
													{profile.firstName.substring(0, 1)}
												</span>
											) : (
												<Icon name='account_circle' />
											)}
											<span
												className='ml-2.5 hidden text-xs font-semibold leading-tight 2xl:inline-flex 2xl:max-w-[17ch] 2xl:overflow-hidden 2xl:whitespace-nowrap 2xl:text-right 2xl:align-middle'
												data-sentry-mask={true}
											>
												{userSignedIn && isLogged ? <span>Hi, {profile.firstName}</span> : <span>Sign In</span>}
											</span>
										</button>

										{showAccounts && (
											<div
												className={`siteAccount absolute top-[97%] z-[15] w-[calc(100%-20px)] border border-gray-15 bg-white p-4 shadow-md max-sm:left-1/2 max-sm:-translate-x-1/2 sm:-right-[100px] xl:right-0 ${userSignedIn ? 'sm:w-[275px]' : 'sm:w-[360px]'}`}
												ref={accountRef}
											>
												<SiteAccount
													userSignedIn={userSignedIn && isLogged}
													onAuthCloseModal={onAuthCloseModal}
													accountData={compData.account}
												/>
											</div>
										)}
									</>
								</Column>

								{/* Cart Icon */}
								{isEcommerce && (
									<Column
										className='cartCol'
										xs='auto'
										id='header-cart'
									>
										<div className='group/cart h-full'>
											<a
												className='inline-flex h-[54px] w-[50px] items-center justify-center leading-none no-underline sm:h-full sm:w-full sm:px-5 sm:py-4 lg:px-4 xl:border-l xl:border-l-gray-15'
												href='/shop/cart/'
											>
												{cartItemCount > 0 && <span className='absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-lg bg-primary text-[10px] leading-none sm:right-2 sm:top-2 xl:right-1.5 xl:top-1.5'>{cartItemCount}</span>}
												<Icon name='shopping_cart' />
												<span className='sr-only'>Cart</span>
											</a>
											{!hideHoverCart && cartItemCount > 0 && <CartList className='invisible opacity-0 group-focus-within/cart:visible group-focus-within/cart:opacity-100 group-hover/cart:visible group-hover/cart:opacity-100 motion-safe:transition-opacity max-xl:hidden' />}
										</div>
									</Column>
								)}

								{/* Hamburger */}
								<Column
									xs='auto'
									className='mobileMenuCol xl:hidden'
								>
									<button
										id='mobileMenu-close'
										className='inline-flex h-[54px] w-[50px] items-center justify-center leading-none sm:h-full sm:w-full sm:px-5 sm:py-4 lg:px-4 xl:border-l xl:border-l-gray-15'
										onClick={onHamburgerClick}
										aria-expanded={showMobileMenu}
										aria-controls='js-mobileMenu'
									>
										<Icon
											name={!showMobileMenu ? 'menu' : 'close'}
											size={30}
										/>
										<span className='sr-only'>Menu</span>
									</button>
								</Column>
							</Row>
						</Column>
					</Row>
				</Container>
			</header>

			<MobileMenu
				data={compData}
				showMenu={showMobileMenu}
			/>

			{showMobileMenu && (
				<button
					aria-label='Nav Backdrop'
					onClick={onHamburgerClick}
					className='mobileMenu-overlay fixed left-0 top-0 z-[19] h-screen w-screen bg-black/40 backdrop-blur-lg xl:hidden'
				/>
			)}
		</>
	);
};

Header.propTypes = {
	showToggle: PropTypes.bool,
	signInToggle: PropTypes.bool,
	signInState: PropTypes.object,
	hideHoverCart: PropTypes.bool,
};

export default Header;

export const query = graphql`
	{
		contentstackMenu(uid: { eq: "bltbee0047e6c1938a1" }) {
			home_icon {
				...CTA
			}
			toggle {
				homeowner {
					...CTA
				}
				pro {
					...CTA
				}
			}
			global_nav {
				cta {
					...CTA
				}
			}
			account {
				cta {
					...CTA
				}
				create_account
			}
			product_menu {
				menu_type
				primary_heading
				direct_link
				secondary_nav {
					secondary_heading
					direct_link
					category {
						category_type
						heading
						direct_link
						group {
							file {
								url
								gatsbyImageData(quality: 90)
							}
							hover {
								url
								gatsbyImageData(quality: 90)
							}
							heading
							subheading
							cta {
								...CTA
							}
						}
						entries {
							...CTA
						}
						materials {
							cta_heading {
								...CTA
							}
							entries {
								...CTA
							}
						}
					}
					shop_all_cta {
						...CTA
					}
				}
			}
		}
	}
`;
